// frontend/src/components/admin/detail-stack.ts
// ───────────────────────────────────────────────────────────────────
// Navigation « pile » de l'admin (ADR-026) : une fiche (produit, section,
// carte) s'ouvre en panneau PAR-DESSUS sa liste, qui reste affichée derrière
// — on retrouve donc la liste exactement où on l'avait laissée.
//
// Les URLs ne changent pas : le lien porte `state.backgroundLocation` (la
// liste) et le routeur rend la liste à cet emplacement + la fiche en
// surcouche. Sans cet état (lien direct, rechargement, favori), la fiche
// s'affiche en page complète comme avant.
// ───────────────────────────────────────────────────────────────────

import { createContext, useCallback, useContext } from 'react';
import { useLocation, useNavigate, type Location, type NavigateOptions } from 'react-router-dom';

/** Fournie par `<StackPanel>` : ferme la fiche avec l'animation inverse. */
export const DetailCloseContext = createContext<(() => void) | null>(null);

export interface DetailLinkState {
  backgroundLocation: Location;
}

/** `state` à passer aux liens qui ouvrent une fiche depuis une liste. */
export function useDetailLinkState(): DetailLinkState {
  return { backgroundLocation: useLocation() };
}

/** Emplacement de la liste à rendre en fond, si la fiche est ouverte en surcouche. */
export function getBackgroundLocation(state: unknown): Location | null {
  if (typeof state !== 'object' || state === null) return null;
  const bg = (state as Partial<DetailLinkState>).backgroundLocation;
  return bg !== undefined && typeof bg.pathname === 'string' ? bg : null;
}

/**
 * Remplace `useNavigate` dans les fiches : en surcouche, « revenir à la liste »
 * dépile (animation inverse) ; en page complète, navigue vers `routeBack` comme avant.
 */
export function useDetailNavigate(): (to: string, options?: NavigateOptions) => void {
  const navigate = useNavigate();
  const close = useContext(DetailCloseContext);
  return useCallback(
    (to: string, options?: NavigateOptions) => {
      if (close !== null) {
        close();
        return;
      }
      void navigate(to, options);
    },
    [navigate, close],
  );
}
